<template>
    <a
        class="lds-popup-offers lds-popup-offers-link"
        target="_blank"
        :href="offer.url"
        :class="positionAlignment"
        :style="widgetColors"
    >
        <div class="lds-popup-offers__block-logo">
            <icon-advertising />
            <div class="lds-popup-offers__logo">
                <img
                    :src="offer.logoUrl"
                    :alt="offer.siteName"
                >
            </div>
            <p
                class="lds-popup-offers__utp"
                v-if="widgetTitle"
            >
                {{ widgetTitle }}
            </p>
        </div>
        <div class="lds-popup-offers__meta">
            <div
                class="lds-popup-offers__item"
                v-if="offer.params?.amount"
            >
                <p class="lds-popup-offers__name">{{ offer.params.amount.name }}</p>
                <p class="lds-popup-offers__value">{{ offer.params.amount.string }} {{ currencySymbol }}</p>
            </div>
            <div
                class="lds-popup-offers__item"
                v-if="offer.params?.period"
            >
                <p class="lds-popup-offers__name">{{ offer.params.period.name }}</p>
                <p class="lds-popup-offers__value">{{ offer.params.period.string }} дн.</p>
            </div>
            <div
                class="lds-popup-offers__item"
                v-if="offer.params?.age"
            >
                <p class="lds-popup-offers__name">{{ offer.params.age.name }}</p>
                <p class="lds-popup-offers__value">{{ offer.params.age.string }} лет</p>
            </div>
        </div>
        <button class="lds-popup-offers__btn">{{ textButton }}</button>
        <button class="lds-popup-offers__close" @click.prevent="$emit('closeWidget')">
            <icon-base icon-color="#303446">
                <icon-close />
            </icon-base>
        </button>
    </a>
</template>

<script setup>
import {computed, inject, toRef} from 'vue';
import useThemes from './composables/useThemes.js';
import IconBase from '../../common/icons/IconBase.vue';
import IconClose from '../../common/icons/IconClose.vue';
import IconAdvertising from "../../common/icons/IconAdvertising.vue";

const props = defineProps({
    settings: {
        type: Object,
        default: () => {
        },
    },

    offers: {
        type: Array,
        required: true,
    },

    widgetTitle: {
        type: String,
        default: '',
    },

    textButton: {
        type: String,
        default: 'Получить деньги',
    },
});

const offer = computed(() => props.offers[0]);

const settings = toRef(props, 'settings');
const { widgetColors } = useThemes({ settings });

const currencySymbol = inject('currencySymbol');

const positionAlignment = computed(() => {
    return props.settings.alignment === 'bottom' ?
        'lds-popup-offers_position_bottom' :
        'lds-popup-offers_position_top';
});

</script>

<style lang="scss" scoped>
.lds-popup-offers {
    $this: &;

    position: fixed;
    left: 50%;

    max-width: 1296px;
    width: 100%;
    display: flex;
    grid-gap: 16px;
    gap: 16px;
    align-items: center;
    padding: 16px 56px 16px 16px;
    box-sizing: border-box !important;

    font-weight: 500 !important;
    text-decoration: none !important;

    background-color: var(--lw-offer-bg);
    border-radius: 16px !important;
    box-shadow: var(--lw-offer-box-shadow);

    transform: translate(-50%, 0);

    z-index: 100;

    text-align: left;

    * {
        box-sizing: border-box !important;
        font-family: var(--lw-font-family), 'Arial', sans-serif !important;
        outline: none !important;
    }

    &:hover {
        #{$this}__btn {
            color: var(--lw-btn-hover-color);
            background-color: var(--lw-btn-hover-bg);
        }
    }

    &:active {
        #{$this}__btn {
            color: var(--lw-btn-active-color);
            background-color: var(--lw-btn-active-bg);
        }
    }

    &_position_top {
        top: 24px;
    }

    &_position_bottom {
        bottom: 24px;
    }

    &__block-logo {
        display: flex;
        grid-gap: 16px;
        gap: 16px;
        align-items: center;
        position: relative;
    }

    &__logo {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        width: 163px;
        height: 51px;
        padding: 4px;

        border-radius: 8px !important;
        background-color: var(--lw-offer-bg-logo);

        img {
            max-width: 100%;
            max-height: 100%;
        }
    }

    &__utp {
        width: 266px;
        margin: 0;

        font-size: 16px !important;
        line-height: 1.18;
        font-weight: 800 !important;
        color: var(--lw-offer-usp-color);
    }

    &__meta {
        display: flex;
        grid-gap: 40px;
        gap: 40px;
        align-items: center;
    }

    &__item {}

    &__name {
        margin: 0 !important;

        font-size: 12px !important;
        font-weight: 400 !important;
        line-height: 1.25;
        letter-spacing: .03em;
        text-transform: uppercase;
        color: var(--lw-offer-params-name);
    }

    &__value {
        margin: 2px 0 0 !important;

        font-size: 14px !important;
        font-weight: 700 !important;
        line-height: 1.57;
        color: var(--lw-offer-params);
    }

    &__btn {
        min-height: 47px;
        margin-left: auto;
        padding: 0 24px;
        outline: 0;

        font-size: 12px !important;
        font-weight: 700 !important;
        line-height: 1.21;
        text-transform: uppercase;
        text-decoration: none !important;
        letter-spacing: .06em;
        color: var(--lw-btn-color);

        border-radius: 8px !important;
        background-color: var(--lw-btn-bg);
        border: 0 !important;

        cursor: pointer;

        &:hover {
            color: var(--lw-btn-hover-color);
            background-color: var(--lw-btn-hover-bg);
        }

        &:active {
            color: var(--lw-btn-active-color);
            background-color: var(--lw-btn-active-bg);
        }
    }

    &__close {
        position: absolute;
        right: 8px;
        top: 8px;
        z-index: 105;

        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        width: 32px;
        height: 32px;
        padding: 0;
        outline: none;

        border: 0 !important;
        background-color: transparent;

        cursor: pointer;
    }

    @container (width < 1366px) {
        max-width: calc(100% - 70px);
    }

    @media (max-width: 1366px) {
        max-width: calc(100% - 70px);
    }

    @container (width < 1080px) {
        &__utp {
            width: 137px;
        }

        &__meta {
            grid-gap: 16px;
            gap: 16px;
        }

        &__btn {
            padding: 16px;
        }
    }

    @media (max-width: 1080px) {
        &__utp {
            width: 137px;
        }

        &__meta {
            grid-gap: 16px;
            gap: 16px;
        }

        &__btn {
            padding: 16px;
        }
    }

    @container (width < 960px) {
        max-width: calc(100% - 32px);
        padding-right: 16px;

        &__logo {
            width: 132px;
            height: 42px;
        }

        &__utp {
            font-size: 12px !important;
            line-height: 1.3333;
        }

        &__item:nth-child(3) {
            display: none;
        }

        &__name {
            line-height: 1;
        }

        &__value {
            font-size: 12px !important;
            line-height: 1.5;
        }

        &__btn {
            margin-right: 40px;
            width: min-content;
        }

        &__close {
            top: 16px;
            right: 16px;

            width: 24px;
            height: 24px;
        }
    }

    @media (max-width: 960px) {
        max-width: calc(100% - 32px);
        padding-right: 16px;

        &__logo {
            width: 132px;
            height: 42px;
        }

        &__utp {
            font-size: 12px !important;
            line-height: 1.3333;
        }

        &__item:nth-child(3) {
            display: none;
        }

        &__name {
            line-height: 1;
        }

        &__value {
            font-size: 12px !important;
            line-height: 1.5;
        }

        &__btn {
            margin-right: 40px;
            width: min-content;
        }

        &__close {
            top: 16px;
            right: 16px;

            width: 24px;
            height: 24px;
        }
        .widget-advertising-icon{
          top: -19px;
          left: -2px;
        }
    }

    @container (width < 740px) {
        display: grid;
        grid-template-columns: 1fr auto;
        grid-gap: 4px 12px;
        gap: 4px 12px;
        width: 100%;
        max-width: 100%;

        &_position_top {
            top: 0;

            border-top-left-radius: 0 !important;
            border-top-right-radius: 0 !important;
        }

        &_position_bottom {
            bottom: 0;

            border-bottom-left-radius: 0 !important;
            border-bottom-right-radius: 0 !important;
        }

        &__block-logo {
            padding-bottom: 4px;
        }

        &__logo {
            width: 103px;
            height: 34px;
        }

        &__utp {
            width: auto;
        }

        &__meta {
            padding-top: 8px;
            grid-column: 1 / 2;

            border-top: 1px solid var(--lw-offer-params-border) !important;
        }

        &__btn {
            grid-column: 2 / 3;
            grid-row: 1 / 3;
            max-width: 103px;
            padding: 13px 12px;
            margin-right: 0;
            margin-top: 24px;

            line-height: 1.5;
        }

        &__close {
            top: 8px;
        }

        .widget-advertising-icon{
          top: -13px;
          left: -11px;
        }
    }

    @media (max-width: 740px) {
        display: grid;
        grid-template-columns: 1fr auto;
        grid-gap: 4px 12px;
        gap: 4px 12px;
        width: 100%;
        max-width: 100%;

        &_position_top {
            top: 0;

            border-top-left-radius: 0 !important;
            border-top-right-radius: 0 !important;
        }

        &_position_bottom {
            bottom: 0;

            border-bottom-left-radius: 0 !important;
            border-bottom-right-radius: 0 !important;
        }

        &__block-logo {
            padding-bottom: 4px;
        }

        &__logo {
            width: 103px;
            height: 34px;
        }

        &__utp {
            width: auto;
        }

        &__meta {
            padding-top: 8px;
            grid-column: 1 / 2;

            border-top: 1px solid var(--lw-offer-params-border) !important;
        }

        &__btn {
            grid-column: 2 / 3;
            grid-row: 1 / 3;
            padding: 13px 12px;
            margin-right: 0;
            margin-top: 24px;

            line-height: 1.5;
        }

        &__close {
            top: 8px;
        }
    }
}
.widget-advertising-icon{
  position: absolute;
  top: -9px;
  left: -9px;
}
</style>